import type {
  Item,
  ColorChartResponse,
  Favorite,
  Order,
  OrderHistory,
  PurchasesResponse,
} from '@/api/@types'

/**
 * クエリ文字列を生成する
 * ※住所などにタブや `&` `=` 等の文字が含まれる場合、URLにそのまま連結すると
 *   URLパーサによって除去・解釈が変わってしまうため必ずエンコードする
 */
const buildQueryString = (params: Record<string, string>) =>
  Object.entries(params)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&')

export const ROUTE = {
  // TODO: エラーコード別にページを用意すると仮定。/errorでまとめる際に見直し
  '401': '/401',
  '403': '/403',
  '404': '/404',
  '500': '/500',
  top: '/',
  login: '/login',
  selectStore: '/select-store',
  employeeLogin: '/employee-login',
  forgetPassword: '/forget-password',
  resetPassword: '/reset-password',
  account: '/account',
  changeEmailAuth: '/change-email-auth',
  search: '/search',
  backorders: '/backorders',
  searchBarcode: '/search/barcode',
  searchCategories: '/search/categories',
  searchBrands: '/search/brands',
  items: '/items',
  productHistory: '/product-history',
  itemDetail: (itemCode: Item['itemCode']) => `/items/${itemCode}`,
  lineups: (lineupCode: Item['lineupCode']) => `/lineups/${lineupCode}/items`,
  colorCharts: (groupId: ColorChartResponse['groupId']) =>
    `/color-charts/${groupId}`,
  favorites: '/favorites',
  favoriteDetail: (
    setNo: Favorite['setNo'],
    storeCode: Favorite['storeCode']
  ) => `/favorites/detail?setNo=${setNo}&storeCode=${storeCode}`,
  cart: '/cart',
  cartComplete: '/cart/complete',
  orderDetail: ({ orderNo }: { orderNo: Order['orderNo'] }) =>
    `/orders/${orderNo}`,
  orderHistories: '/order-histories',
  orderHistoriesDetailBySalesOrderNo: ({
    salesOrderNo,
  }: {
    salesOrderNo: string
  }) => `/order-histories/detail?salesOrderNo=${salesOrderNo}`,
  orderHistoriesDetailByShippingInfo: ({
    desiredDeliveryDate,
    zipCode,
    address1,
    address2,
    tel,
    deliveryName,
    storeCode,
  }: OrderHistory['shippingInfo'] &
    Pick<OrderHistory, 'desiredDeliveryDate' | 'storeCode'>) =>
    `/order-histories/detail?${buildQueryString({
      deliveryDate: desiredDeliveryDate,
      zipCode,
      address1,
      address2,
      tel,
      deliveryName,
      storeCode,
    })}`,
  orderImport: '/order-import',
  purchases: '/purchases',
  purchaseDetail: ({
    systemType,
    ecSalesOrderNo,
  }: Pick<PurchasesResponse, 'systemType' | 'ecSalesOrderNo'>) =>
    `/purchases/${systemType}?ecSalesOrderNo=${encodeURIComponent(
      ecSalesOrderNo
    )}`,
  purchasedInfo: '/purchased-info',
  purchasedInfoSalesDetail: '/purchased-info/sales-detail',
  purchasedInfoBilledAmounts: '/purchased-info/billed-amounts',
  purchasedInfoMakers: '/purchased-info/makers',
  purchasedInfoMakersStores: '/purchased-info/makers-stores',
  purchasedInfoStores: '/purchased-info/stores',
  purchasedInfoStoresSalesTypes: '/purchased-info/stores-sales-types',
  purchasedInfoStoresMakersSalesTypes:
    '/purchased-info/stores-makers-sales-types',
  guides:
    'https://supportaltairlifekarte.lifekarte.com/hc/ja/categories/54194714133657-%E3%81%94%E5%88%A9%E7%94%A8%E3%82%AC%E3%82%A4%E3%83%89',
  faq: 'https://supportaltairlifekarte.lifekarte.com/hc/ja/categories/54194716574873-FAQ-%E3%82%88%E3%81%8F%E3%81%82%E3%82%8B%E3%81%94%E8%B3%AA%E5%95%8F',
  help: 'https://supportaltairlifekarte.lifekarte.com/hc/ja',
  guidesSearch: ({ q }: { q?: string }) =>
    `/guides/search${q ? `?q=${q}` : ''}`,
  guideDetail: (id: string) => `/guides/${id}`,
  contact: '/contact',
  contactForm: (content: 'password' | 'locked-account' | 'app' | 'other') =>
    `/contact/${content}`,
  contactComplete: '/contact/complete',
  termsOfService:
    'https://altairpre-login.zendesk.com/hc/ja/articles/63450510824601-%E3%81%94%E5%88%A9%E7%94%A8%E8%A6%8F%E7%B4%84',
  privacyPolicy:
    'https://altairpre-login.zendesk.com/hc/ja/articles/63450374681497-%E3%83%97%E3%83%A9%E3%82%A4%E3%83%90%E3%82%B7%E3%83%BC%E3%83%9D%E3%83%AA%E3%82%B7%E3%83%BC',
  tokushoho:
    'https://altairpre-login.zendesk.com/hc/ja/articles/63450619011737-%E7%89%B9%E5%AE%9A%E5%95%86%E5%8F%96%E5%BC%95%E6%B3%95%E3%81%AB%E5%9F%BA%E3%81%A5%E3%81%8F%E8%A1%A8%E8%A8%98',
  invoice: '/invoice',
}

export const EXTERNAL_ROUTE = {
  corporateSite: process.env.NEXT_PUBLIC_CORPORATE_SITE_URL || '',
}
